import type { SanitizedQuestionItem } from "../../../shared/types";
import { AttentionRenderer } from "./AttentionRenderer";
import {
  AttentionTargetRenderer,
  BlueCircleDotComparisonMatrixRenderer,
  CircleHalfFillMatrixRenderer,
  CircleSegmentUnionMatrixRenderer,
  ColorBlockSequenceRenderer,
  ColorGridTransformMatrixRenderer,
  CubeFoldingLiteRenderer,
  DotCornerRenderer,
  DotMovementSequenceRenderer,
  FigureCompletionRenderer,
  HexXorRotationMatrixRenderer,
  MiniGridSymbolCycleMatrixRenderer,
  MiniGridSymbolCompletionRenderer,
  MissingPartTileRenderer,
  NeonSymbolMatrix5x5Renderer,
  NeonTileOddOneOutRenderer,
  PieSliceUnionMatrixRenderer,
  PositionGridRenderer,
  QuadrantColorShapeMatrixRenderer,
  RoundedDomeColorMatrixRenderer,
  SymbolGridStepSequenceRenderer,
  TopViewIdentificationRenderer,
  VisualEquationRenderer
} from "./GeneratedVisualRenderer";
import { ImageAssetRenderer } from "./ImageAssetRenderer";
import { MatrixRenderer } from "./MatrixRenderer";
import { SequenceRenderer } from "./SequenceRenderer";
import { SpatialRenderer } from "./SpatialRenderer";
import { TextLogicRenderer } from "./TextLogicRenderer";

export function VisualQuestionRenderer({
  question,
  selectedOption,
  onSelect,
  readOnly = false,
  revealCorrectOption
}: {
  question: SanitizedQuestionItem;
  selectedOption?: string | null;
  onSelect?: (optionId: string) => void;
  readOnly?: boolean;
  revealCorrectOption?: string;
}) {
  if (question.renderer === "matrix_3x3") return <MatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "shape_sequence") return <SequenceRenderer visualSpec={question.visualSpec} shapeMode />;
  if (question.renderer === "number_sequence") return <SequenceRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "spatial_rotation") return <SpatialRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "attention_grid") return <AttentionRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "position_grid_movement") return <PositionGridRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "visual_equation") return <VisualEquationRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "dot_corner_relation") return <DotCornerRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "dot_movement_sequence") return <DotMovementSequenceRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "color_block_sequence") {
    return <ColorBlockSequenceRenderer visualSpec={question.visualSpec} selectedOption={selectedOption} onSelect={onSelect} readOnly={readOnly} revealCorrectOption={revealCorrectOption} />;
  }
  if (question.renderer === "circle_half_fill_matrix") return <CircleHalfFillMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "circle_segment_union_matrix") return <CircleSegmentUnionMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "pie_slice_union_matrix") return <PieSliceUnionMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "top_view_identification") return <TopViewIdentificationRenderer />;
  if (question.renderer === "symbol_grid_step_sequence") return <SymbolGridStepSequenceRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "blue_circle_dot_comparison_matrix") return <BlueCircleDotComparisonMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "color_grid_transform_matrix") return <ColorGridTransformMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "neon_symbol_matrix_5x5") return <NeonSymbolMatrix5x5Renderer visualSpec={question.visualSpec} />;
  if (question.renderer === "neon_tile_odd_one_out_sequence") {
    return <NeonTileOddOneOutRenderer visualSpec={question.visualSpec} selectedOption={selectedOption} onSelect={onSelect} readOnly={readOnly} revealCorrectOption={revealCorrectOption} />;
  }
  if (question.renderer === "rounded_dome_color_matrix") return <RoundedDomeColorMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "quadrant_color_shape_matrix_4x3") return <QuadrantColorShapeMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "mini_grid_symbol_cycle_matrix") return <MiniGridSymbolCycleMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "mini_grid_symbol_completion") return <MiniGridSymbolCompletionRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "hex_xor_rotation_matrix") return <HexXorRotationMatrixRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "hidden_target") return <AttentionTargetRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "quick_count") return <AttentionTargetRenderer visualSpec={question.visualSpec} countMode />;
  if (question.renderer === "cube_folding_lite") return <CubeFoldingLiteRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "missing_part_tile") return <MissingPartTileRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "figure_completion") return <FigureCompletionRenderer visualSpec={question.visualSpec} />;
  if (question.renderer === "image_asset") return <ImageAssetRenderer visualSpec={question.visualSpec} />;
  return <TextLogicRenderer visualSpec={question.visualSpec} />;
}
